import React, { ComponentType, Suspense } from 'react';
import { lazyWithRetry } from './lazyWithRetry';

/**
 * Fallback component shown while the icon is loading
 * This is a simple invisible placeholder that maintains the icon's typical dimensions
 */
const IconFallback: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    style={{ display: 'inline-block', verticalAlign: 'middle' }}
  >
    {/* Empty SVG as placeholder */}
  </svg>
);

/**
 * Wrapper for lazy-loaded Carbon icons that provides a fallback
 * This allows icons to be used with styled-components without causing reference errors
 *
 * @param importFn - Function that returns a promise for the icon module
 * @param size - Icon size (16, 20, 24, 32) for the fallback
 *
 * @example
 * const Add16 = lazyLoadCarbonIcon(() => import('@carbon/icons-react/es/add/16'), 16);
 * const StyledIcon = styled(Add16)`color: blue;`; // Works without errors!
 */
export function lazyLoadCarbonIcon(
  importFn: () => Promise<any>,
  size: number = 16,
): ComponentType<any> {
  const LazyIcon = lazyWithRetry(importFn);

  // Return a component that wraps the lazy icon with Suspense
  const IconWithFallback: ComponentType<any> = (props) => (
    <Suspense fallback={<IconFallback size={size} />}>
      <LazyIcon {...props} />
    </Suspense>
  );

  // Preserve displayName for debugging
  IconWithFallback.displayName = 'LazyIcon';

  return IconWithFallback;
}
